import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  manager_name: string | null
}

interface ReportRow {
  id: number
  report_date: string | null
  day_name: string | null
  report_type: string
  leader: string | null
  team: string
  status: number
  status_name: string
  selected: boolean
  row_class: string
}

interface Props {
  case_record: CaseRecord
  reports: ReportRow[]
}

export default function SelectFinalReports({ case_record, reports }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: "Select for Final Report",
      href: `/cases/${case_record.id}/reports/select_final_reports`,
    },
  ]

  const [selections, setSelections] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {}
    reports.forEach((r) => {
      initial[r.id] = r.selected
    })
    return initial
  })

  const [saving, setSaving] = useState(false)
  const [building, setBuilding] = useState<string | null>(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const allSelected =
    reports.length > 0 && reports.every((r) => selections[r.id])

  const toggleAll = () => {
    const newValue = !allSelected
    const updated: Record<number, boolean> = {}
    reports.forEach((r) => {
      updated[r.id] = newValue
    })
    setSelections(updated)
  }

  const toggleOne = (id: number) => {
    setSelections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleSave = () => {
    setSaving(true)
    const data: Record<string, string> = {}
    Object.entries(selections).forEach(([id, selected]) => {
      data[id] = String(selected)
    })
    router.patch(
      `/cases/${case_record.id}/reports/update_final_reports`,
      { reports: data },
      { onFinish: () => setSaving(false) },
    )
  }

  const buildDownloadUrl = (format: "by_report" | "by_day") => {
    const params = new URLSearchParams()
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    const qs = params.toString()
    const action =
      format === "by_report"
        ? "final_report_by_report_word"
        : "final_report_by_day_word"
    return `/cases/${case_record.id}/report_downloads/${action}${qs ? `?${qs}` : ""}`
  }

  const handleBuildWord = (format: "by_report" | "by_day") => {
    setBuilding(format)
    // Save selections first, then trigger download
    const data: Record<string, string> = {}
    Object.entries(selections).forEach(([id, selected]) => {
      data[id] = String(selected)
    })
    router.patch(
      `/cases/${case_record.id}/reports/update_final_reports`,
      { reports: data },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          // Trigger file download after save completes
          window.location.href = buildDownloadUrl(format)
          setBuilding(null)
        },
        onError: () => {
          setBuilding(null)
        },
      },
    )
  }

  const anySelected = Object.values(selections).some(Boolean)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`Select Reports for Final Report - ${case_record.case_number}`}
      />

      <div className="p-6">
        <h1 className="mb-2 text-2xl font-semibold">
          {case_record.display_name} Final Report
        </h1>
        <h3 className="mb-4 text-center text-lg font-medium">
          Select Reports to include in Final Report
        </h3>

        {reports.length > 0 ? (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-center gap-4">
              <span className="text-hpi-med-gray text-sm">Done?</span>
              <button
                onClick={handleSave}
                disabled={saving || building !== null}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <Link
                href={`/cases/${case_record.id}/final_report_preview`}
                target="_blank"
                className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-1.5 text-sm"
              >
                Preview Final Report
              </Link>
              <button
                onClick={() => handleBuildWord("by_report")}
                disabled={building !== null || !anySelected || saving}
                className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-1.5 text-sm disabled:opacity-50"
              >
                {building === "by_report"
                  ? "Building..."
                  : "Build Word Doc by Report"}
              </button>
              <button
                onClick={() => handleBuildWord("by_day")}
                disabled={building !== null || !anySelected || saving}
                className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-1.5 text-sm disabled:opacity-50"
              >
                {building === "by_day"
                  ? "Building..."
                  : "Build Word Doc by Day"}
              </button>
            </div>

            <div className="border-hpi-light-gray bg-hpi-pale-gray mb-4 flex flex-wrap items-end justify-center gap-4 rounded border p-3">
              <div className="text-sm font-medium">Date Range (optional)</div>
              <div className="flex items-center gap-2">
                <label
                  htmlFor="start_date"
                  className="text-hpi-med-gray text-sm"
                >
                  Start
                </label>
                <input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="end_date" className="text-hpi-med-gray text-sm">
                  End
                </label>
                <input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
                />
              </div>
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate("")
                    setEndDate("")
                  }}
                  className="text-hpi-med-gray hover:text-hpi-dark-gray text-sm underline"
                >
                  Clear dates
                </button>
              )}
            </div>

            <div className="mx-auto max-w-5xl">
              <table className="border-hpi-light-gray w-full border-collapse border text-sm">
                <thead>
                  <tr className="bg-hpi-pale-gray">
                    <th className="border-hpi-light-gray w-12 border px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="h-4 w-4 cursor-pointer"
                        title="Select all"
                      />
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Date
                    </th>
                    <th className="border-hpi-light-gray hidden border px-4 py-2 text-left md:table-cell">
                      Type
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Team
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-right">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr
                      key={report.id}
                      className={
                        selections[report.id]
                          ? "hover:bg-hpi-pale-blue"
                          : "bg-hpi-pale-gray/50 text-hpi-med-gray hover:bg-hpi-pale-blue/50"
                      }
                    >
                      <td className="border-hpi-light-gray border px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selections[report.id] ?? false}
                          onChange={() => toggleOne(report.id)}
                          className="h-4 w-4 cursor-pointer"
                        />
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 whitespace-nowrap">
                        <Link
                          href={`/cases/${case_record.id}/reports/${report.id}`}
                          target="_blank"
                          className="text-hpi-active-blue hover:underline"
                        >
                          {report.day_name && (
                            <div className="text-hpi-med-gray text-xs">
                              {report.day_name}
                            </div>
                          )}
                          <div>{formatDate(report.report_date)}</div>
                        </Link>
                      </td>
                      <td className="border-hpi-light-gray hidden border px-4 py-2 md:table-cell">
                        <Link
                          href={`/cases/${case_record.id}/reports/${report.id}`}
                          target="_blank"
                          className="text-hpi-active-blue hover:underline"
                        >
                          {report.report_type}
                        </Link>
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        <Link
                          href={`/cases/${case_record.id}/reports/${report.id}`}
                          target="_blank"
                          className="text-hpi-active-blue hover:underline"
                        >
                          {report.leader && (
                            <span className="font-bold">{report.leader}</span>
                          )}
                          {report.leader && report.team && <span>, </span>}
                          {report.team}
                        </Link>
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 text-right">
                        <Link
                          href={`/cases/${case_record.id}/reports/${report.id}`}
                          target="_blank"
                          className="text-hpi-active-blue hover:underline"
                        >
                          {report.status_name}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 text-center">
                <button
                  onClick={handleSave}
                  disabled={saving || building !== null}
                  className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-6 py-2 text-white disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">
            No reports are available
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}/reports`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Reports
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
